import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Plus, ChevronRight, Calendar, User, Building2, FileWarning, Clock, CheckCircle2, Save, Download, Home, Loader2, BarChart3, Paperclip, Upload, X, Image, FileText, Trash2, Link2, Copy, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { formatDate } from '@/utils/formatters';
import { useAuth } from '@/context/AuthContext';
import * as XLSX from 'xlsx';
import NcrDashboard from './NcrDashboard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// NCR Stages for Kanban columns
const NCR_STAGES = [
  "New NCR Logged",
  "Containment Action",
  "Investigation / Root Cause Analysis",
  "Corrective Action Defined",
  "Corrective Action Implemented",
  "Verification of Effectiveness",
  "Closed"
];

// Stage colors
const STAGE_COLORS = {
  "New NCR Logged": "border-red-600 bg-red-950/30",
  "Containment Action": "border-orange-600 bg-orange-950/30",
  "Investigation / Root Cause Analysis": "border-yellow-600 bg-yellow-950/30",
  "Corrective Action Defined": "border-blue-600 bg-blue-950/30",
  "Corrective Action Implemented": "border-purple-600 bg-purple-950/30",
  "Verification of Effectiveness": "border-cyan-600 bg-cyan-950/30",
  "Closed": "border-green-600 bg-green-950/30"
};

// Severity badge colors
const SEVERITY_COLORS = {
  "Critical": "bg-red-600 text-white",
  "Medium": "bg-orange-500 text-white",
  "Low": "bg-yellow-500 text-black"
};

const NcrKanbanBoard = ({ onNcrChange, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ncrs, setNcrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [draggedNcr, setDraggedNcr] = useState(null);
  const [activeTab, setActiveTab] = useState('kanban');
  const fileInputRef = useRef(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  const handleBack = () => {
    if (onClose) onClose();
    else navigate('/');
  };
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedNcr, setSelectedNcr] = useState(null);
  const [editFormData, setEditFormData] = useState(null); // Local state for editing
  const [saving, setSaving] = useState(false);
  const [showExternalLinkDialog, setShowExternalLinkDialog] = useState(false);
  const [externalLink, setExternalLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  
  // Create form state
  const [newNcr, setNewNcr] = useState({
    ncr_type: '',
    source: '',
    severity: '',
    raised_by: '',
    department: '',
    supplier: '',
    work_order_reference: '',
    customer_reference: '',
    description: '',
    responsible_person: '',
    due_date: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ncrsRes, configRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/ncrs`),
        axios.get(`${BACKEND_URL}/api/ncr/config`)
      ]);
      setNcrs(ncrsRes.data);
      setConfig(configRes.data);
      if (onNcrChange) onNcrChange(ncrsRes.data);
    } catch (error) {
      console.error('Error fetching NCR data:', error);
      toast.error('Failed to load NCR data');
    } finally {
      setLoading(false);
    }
  };

  const getNcrsForStage = (stage) => {
    return ncrs.filter(ncr => ncr.status === stage);
  };

  const handleDragStart = (e, ncr) => {
    setDraggedNcr(ncr);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, newStage) => {
    e.preventDefault();
    
    if (!draggedNcr) return;
    
    if (draggedNcr.status === newStage) {
      setDraggedNcr(null);
      return;
    }

    try {
      await axios.put(`${BACKEND_URL}/api/ncrs/${draggedNcr.ncr_number}?updated_by=User`, {
        status: newStage
      });
      
      toast.success(`NCR ${draggedNcr.ncr_number} moved to ${newStage}`);
      fetchData();
    } catch (error) {
      console.error('Error updating NCR:', error);
      toast.error('Failed to update NCR status');
    }
    
    setDraggedNcr(null);
  };

  const handleCreateNcr = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!newNcr.ncr_type || !newNcr.source || !newNcr.severity || !newNcr.raised_by || !newNcr.department || !newNcr.description) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await axios.post(`${BACKEND_URL}/api/ncrs`, newNcr);
      toast.success(`NCR ${response.data.ncr_number} created successfully`);
      setCreateDialogOpen(false);
      setNewNcr({
        ncr_type: '',
        source: '',
        severity: '',
        raised_by: '',
        department: '',
        supplier: '',
        work_order_reference: '',
        customer_reference: '',
        description: '',
        responsible_person: '',
        due_date: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating NCR:', error);
      toast.error('Failed to create NCR');
    }
  };

  const handleNcrClick = (ncr) => {
    setSelectedNcr(ncr);
    // Initialize edit form data with current NCR values
    setEditFormData({
      containment: { ...ncr.containment },
      five_why: { ...ncr.five_why },
      corrective_action: { ...ncr.corrective_action },
      verification: { ...ncr.verification }
    });
    setDetailDialogOpen(true);
  };

  // Update local form state (no API call)
  const handleFormChange = (field, value) => {
    if (!editFormData) return;
    
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setEditFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent] || {}),
          [child]: value
        }
      }));
    } else {
      setEditFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  // Save all changes to API
  const handleSaveChanges = async () => {
    if (!selectedNcr || !editFormData) return;
    
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/ncrs/${selectedNcr.ncr_number}?updated_by=User`, editFormData);
      toast.success('NCR updated successfully');
      fetchData();
      
      // Refresh selected NCR
      const updatedNcr = await axios.get(`${BACKEND_URL}/api/ncrs/${selectedNcr.ncr_number}`);
      setSelectedNcr(updatedNcr.data);
      setEditFormData({
        containment: { ...updatedNcr.data.containment },
        five_why: { ...updatedNcr.data.five_why },
        corrective_action: { ...updatedNcr.data.corrective_action },
        verification: { ...updatedNcr.data.verification }
      });
    } catch (error) {
      console.error('Error updating NCR:', error);
      toast.error('Failed to update NCR');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateNcr = async (field, value) => {
    if (!selectedNcr) return;
    
    try {
      const updateData = {};
      
      // Handle nested fields
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updateData[parent] = { [child]: value };
      } else {
        updateData[field] = value;
      }
      
      await axios.put(`${BACKEND_URL}/api/ncrs/${selectedNcr.ncr_number}?updated_by=User`, updateData);
      toast.success('NCR updated successfully');
      fetchData();
      
      // Refresh selected NCR
      const updatedNcr = await axios.get(`${BACKEND_URL}/api/ncrs/${selectedNcr.ncr_number}`);
      setSelectedNcr(updatedNcr.data);
    } catch (error) {
      console.error('Error updating NCR:', error);
      toast.error('Failed to update NCR');
    }
  };

  const handleAdvanceStage = async () => {
    if (!selectedNcr) return;
    
    const currentIndex = NCR_STAGES.indexOf(selectedNcr.status);
    if (currentIndex < NCR_STAGES.length - 1) {
      const nextStage = NCR_STAGES[currentIndex + 1];
      await handleUpdateNcr('status', nextStage);
    }
  };

  // Download closed NCR as Excel
  const handleDownloadNcr = () => {
    if (!selectedNcr) return;

    try {
      // Create workbook
      const wb = XLSX.utils.book_new();

      // NCR Summary Sheet
      const summaryData = [
        ['NON-CONFORMANCE REPORT'],
        [''],
        ['NCR Number', selectedNcr.ncr_number],
        ['Type', selectedNcr.ncr_type],
        ['Status', selectedNcr.status],
        ['Severity', selectedNcr.severity],
        ['Source', selectedNcr.source],
        ['Department', selectedNcr.department],
        [''],
        ['DATES'],
        ['Date Raised', formatDate(selectedNcr.date_raised)],
        ['Due Date', selectedNcr.due_date ? formatDate(selectedNcr.due_date) : 'N/A'],
        ['Date Closed', selectedNcr.date_closed ? formatDate(selectedNcr.date_closed) : formatDate(new Date().toISOString())],
        [''],
        ['PERSONNEL'],
        ['Raised By', selectedNcr.raised_by],
        ['Responsible Person', selectedNcr.responsible_person || 'N/A'],
        [''],
        ['REFERENCES'],
        ['Work Order Reference', selectedNcr.work_order_reference || 'N/A'],
        ['Customer Reference', selectedNcr.customer_reference || 'N/A'],
        ['Supplier', selectedNcr.supplier || 'N/A'],
        [''],
        ['DESCRIPTION'],
        [selectedNcr.description]
      ];

      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      
      // Set column widths
      summarySheet['!cols'] = [{ wch: 25 }, { wch: 50 }];
      
      XLSX.utils.book_append_sheet(wb, summarySheet, 'NCR Summary');

      // Containment Action Sheet
      const containmentData = [
        ['CONTAINMENT ACTION'],
        [''],
        ['Action Taken', selectedNcr.containment?.action || 'N/A'],
        ['Responsible Person', selectedNcr.containment?.responsible_person || 'N/A'],
        ['Date Completed', selectedNcr.containment?.date_completed ? formatDate(selectedNcr.containment.date_completed) : 'N/A']
      ];

      const containmentSheet = XLSX.utils.aoa_to_sheet(containmentData);
      containmentSheet['!cols'] = [{ wch: 25 }, { wch: 60 }];
      XLSX.utils.book_append_sheet(wb, containmentSheet, 'Containment');

      // Root Cause Analysis Sheet (5 Why)
      const rcaData = [
        ['5 WHY ROOT CAUSE ANALYSIS'],
        [''],
        ['Why 1', selectedNcr.five_why?.why1 || 'N/A'],
        ['Why 2', selectedNcr.five_why?.why2 || 'N/A'],
        ['Why 3', selectedNcr.five_why?.why3 || 'N/A'],
        ['Why 4', selectedNcr.five_why?.why4 || 'N/A'],
        ['Why 5', selectedNcr.five_why?.why5 || 'N/A'],
        [''],
        ['ROOT CAUSE'],
        [selectedNcr.five_why?.root_cause || 'N/A']
      ];

      const rcaSheet = XLSX.utils.aoa_to_sheet(rcaData);
      rcaSheet['!cols'] = [{ wch: 15 }, { wch: 70 }];
      XLSX.utils.book_append_sheet(wb, rcaSheet, 'Root Cause Analysis');

      // Corrective Action Sheet
      const correctiveData = [
        ['CORRECTIVE ACTION'],
        [''],
        ['Action', selectedNcr.corrective_action?.action || 'N/A'],
        ['Responsible Person', selectedNcr.corrective_action?.responsible_person || 'N/A'],
        ['Target Date', selectedNcr.corrective_action?.target_date ? formatDate(selectedNcr.corrective_action.target_date) : 'N/A'],
        ['Date Completed', selectedNcr.corrective_action?.date_completed ? formatDate(selectedNcr.corrective_action.date_completed) : 'N/A'],
        [''],
        ['IMPLEMENTATION NOTES'],
        [selectedNcr.corrective_action?.implementation_notes || 'N/A']
      ];

      const correctiveSheet = XLSX.utils.aoa_to_sheet(correctiveData);
      correctiveSheet['!cols'] = [{ wch: 25 }, { wch: 60 }];
      XLSX.utils.book_append_sheet(wb, correctiveSheet, 'Corrective Action');

      // Verification Sheet
      const verificationData = [
        ['VERIFICATION OF EFFECTIVENESS'],
        [''],
        ['Verified By', selectedNcr.verification?.verified_by || 'N/A'],
        ['Verification Date', selectedNcr.verification?.verification_date ? formatDate(selectedNcr.verification.verification_date) : 'N/A'],
        ['Effective', selectedNcr.verification?.effective ? 'Yes' : 'No'],
        [''],
        ['VERIFICATION NOTES'],
        [selectedNcr.verification?.notes || 'N/A']
      ];

      const verificationSheet = XLSX.utils.aoa_to_sheet(verificationData);
      verificationSheet['!cols'] = [{ wch: 25 }, { wch: 60 }];
      XLSX.utils.book_append_sheet(wb, verificationSheet, 'Verification');

      // Audit Trail Sheet
      if (selectedNcr.audit_trail && selectedNcr.audit_trail.length > 0) {
        const auditData = [
          ['AUDIT TRAIL'],
          [''],
          ['Date', 'Action', 'User', 'Details']
        ];

        selectedNcr.audit_trail.forEach(entry => {
          auditData.push([
            formatDate(entry.timestamp),
            entry.action,
            entry.user,
            entry.details
          ]);
        });

        const auditSheet = XLSX.utils.aoa_to_sheet(auditData);
        auditSheet['!cols'] = [{ wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 50 }];
        XLSX.utils.book_append_sheet(wb, auditSheet, 'Audit Trail');
      }

      // Generate filename and download
      const filename = `${selectedNcr.ncr_number}_Report.xlsx`;
      XLSX.writeFile(wb, filename);
      
      toast.success(`NCR report downloaded: ${filename}`);
    } catch (error) {
      console.error('Error downloading NCR:', error);
      toast.error('Failed to download NCR report');
    }
  };

  // Handle file upload for attachments
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !selectedNcr) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', '');
      formData.append('uploaded_by', user?.name || 'User');

      await axios.post(
        `${BACKEND_URL}/api/ncrs/${selectedNcr.ncr_number}/attachments`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('File uploaded successfully');
      
      // Refresh NCR data
      const updatedNcr = await axios.get(`${BACKEND_URL}/api/ncrs/${selectedNcr.ncr_number}`);
      setSelectedNcr(updatedNcr.data);
      fetchData();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle attachment download
  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/ncrs/${selectedNcr.ncr_number}/attachments/${attachment.attachment_id}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Failed to download attachment');
    }
  };

  // Handle attachment delete
  const handleDeleteAttachment = async (attachment) => {
    if (!confirm(`Delete ${attachment.original_filename}?`)) return;

    try {
      await axios.delete(
        `${BACKEND_URL}/api/ncrs/${selectedNcr.ncr_number}/attachments/${attachment.attachment_id}?deleted_by=${user?.name || 'User'}`
      );
      
      toast.success('Attachment deleted');
      
      // Refresh NCR data
      const updatedNcr = await axios.get(`${BACKEND_URL}/api/ncrs/${selectedNcr.ncr_number}`);
      setSelectedNcr(updatedNcr.data);
      fetchData();
    } catch (error) {
      console.error('Error deleting attachment:', error);
      toast.error('Failed to delete attachment');
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Generate external supplier link
  const handleGenerateExternalLink = async () => {
    if (!selectedNcr) return;
    
    setGeneratingLink(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/ncrs/${selectedNcr.ncr_number}/generate-link`,
        null,
        { params: { supplier_name: selectedNcr.supplier || '', expires_days: 30 } }
      );
      
      const baseUrl = window.location.origin;
      const fullLink = `${baseUrl}/ncr/external/${response.data.token}`;
      setExternalLink({
        ...response.data,
        full_url: fullLink
      });
      setShowExternalLinkDialog(true);
    } catch (error) {
      console.error('Error generating link:', error);
      toast.error('Failed to generate external link');
    } finally {
      setGeneratingLink(false);
    }
  };

  // Copy link to clipboard
  const handleCopyLink = () => {
    if (externalLink?.full_url) {
      navigator.clipboard.writeText(externalLink.full_url);
      toast.success('Link copied to clipboard');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-400" data-testid="ncr-loading" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden text-white" data-testid="ncr-kanban-board">
      {/* Honeycomb Background */}
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="honeycomb-ncr" x="0" y="0" width="56" height="100" patternUnits="userSpaceOnUse">
              <path d="M28 0L56 16.5V49.5L28 66L0 49.5V16.5L28 0Z" fill="none" stroke="rgba(239, 68, 68, 0.12)" strokeWidth="1"/>
              <path d="M28 66L56 82.5V115.5L28 132L0 115.5V82.5L28 66Z" fill="none" stroke="rgba(239, 68, 68, 0.12)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#honeycomb-ncr)" />
        </svg>
      </div>
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(239, 68, 68, 0.06) 0%, transparent 70%)' }} />

      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-slate-950/90 backdrop-blur-xl border-b border-red-500/20">
        <div className="h-full max-w-[1920px] mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={handleBack} className="text-slate-400 hover:text-red-400 hover:bg-red-500/10" data-testid="back-btn">
              <Home className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white uppercase tracking-wide">NCR Manager</h1>
                <p className="text-xs text-slate-500">Non-Conformance Reports</p>
              </div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-2 text-sm">
            <span className="text-slate-500">Home</span>
            <ChevronRight className="w-4 h-4 text-slate-600" />
            <span className="text-red-400 font-medium">NCR</span>
          </nav>
          <div className="flex items-center gap-3">
            {/* Tab Buttons */}
            <div className="flex bg-slate-800/50 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('kanban')}
                className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wider rounded transition-colors ${
                  activeTab === 'kanban' 
                    ? 'bg-red-500/20 text-red-400' 
                    : 'text-slate-400 hover:text-white'
                }`}
                data-testid="tab-kanban"
              >
                Kanban
              </button>
              <button
                onClick={() => setActiveTab('dashboard')}
                className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wider rounded transition-colors flex items-center gap-1 ${
                  activeTab === 'dashboard' 
                    ? 'bg-red-500/20 text-red-400' 
                    : 'text-slate-400 hover:text-white'
                }`}
                data-testid="tab-dashboard"
              >
                <BarChart3 className="w-3 h-3" />
                Dashboard
              </button>
            </div>
            <Button onClick={() => setCreateDialogOpen(true)} className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50 hover:shadow-[0_0_20px_rgba(239,68,68,0.3)]" data-testid="create-ncr-btn">
              <Plus className="w-4 h-4 mr-2" /> Log New NCR
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-20 px-4 sm:px-6 pb-8">
        <div className="max-w-[1920px] mx-auto space-y-6">

      {/* Dashboard Tab Content */}
      {activeTab === 'dashboard' && (
        <NcrDashboard onClose={() => setActiveTab('kanban')} />
      )}

      {/* Kanban Tab Content */}
      {activeTab === 'kanban' && (
        <>
      {/* Summary Stats */}
      <div className="bg-slate-900/60 backdrop-blur-sm border border-red-500/10 rounded-lg px-4 py-3">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {NCR_STAGES.map((stage) => {
            const count = getNcrsForStage(stage).length;
            return (
              <div key={stage} className={`${STAGE_COLORS[stage]} border rounded-lg p-3 text-center`}>
                <p className="text-xs text-slate-400 uppercase tracking-wider truncate" title={stage}>{stage.split(' / ')[0]}</p>
                <p className="text-xl font-bold font-mono text-white">{count}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Kanban Board */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {NCR_STAGES.map((stage) => {
            const stageNcrs = getNcrsForStage(stage);
            
            return (
              <div
                key={stage}
                className="w-72 flex-shrink-0"
                data-testid={`ncr-column-${stage.replace(/\s+/g, '-').toLowerCase()}`}
              >
                {/* Column Header */}
                <div className={`${STAGE_COLORS[stage]} border rounded-sm p-3 mb-3`}>
                  <h3 className="text-xs font-bold text-white uppercase tracking-wider truncate" title={stage}>
                    {stage}
                  </h3>
                  <span className="text-xs text-muted-foreground font-mono">
                    {stageNcrs.length} {stageNcrs.length === 1 ? 'NCR' : 'NCRs'}
                  </span>
                </div>

                {/* Drop Zone */}
                <div
                  className="bg-muted/20 border border-border/50 rounded-sm p-2 min-h-[300px] max-h-[500px] overflow-y-auto"
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, stage)}
                  data-testid={`ncr-drop-zone-${stage.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className="space-y-2">
                    {stageNcrs.map((ncr) => (
                      <div
                        key={ncr.ncr_number}
                        draggable
                        onDragStart={(e) => handleDragStart(e, ncr)}
                        onClick={() => handleNcrClick(ncr)}
                        className="bg-card border border-border rounded-sm p-3 cursor-move hover:border-primary/50 transition-colors"
                        data-testid={`ncr-card-${ncr.ncr_number}`}
                      >
                        {/* NCR Number and Severity */}
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-mono text-white font-bold text-sm">
                            {ncr.ncr_number}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-sm font-bold ${SEVERITY_COLORS[ncr.severity]}`}>
                            {ncr.severity}
                          </span>
                        </div>

                        {/* Type Badge */}
                        <div className="mb-2 flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded-sm font-bold ${
                            ncr.ncr_type === 'External' ? 'bg-purple-950 text-purple-400' : 'bg-blue-950 text-blue-400'
                          }`}>
                            {ncr.ncr_type}
                          </span>
                          {/* Attachment indicator */}
                          {ncr.attachments && ncr.attachments.length > 0 && (
                            <span className="flex items-center gap-1 text-xs text-cyan-400">
                              <Paperclip className="w-3 h-3" />
                              {ncr.attachments.length}
                            </span>
                          )}
                        </div>

                        {/* Details */}
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <FileWarning className="w-3 h-3" />
                            <span className="truncate" title={ncr.source}>{ncr.source}</span>
                          </div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Building2 className="w-3 h-3" />
                            <span>{ncr.department}</span>
                          </div>
                          {ncr.responsible_person && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <User className="w-3 h-3" />
                              <span>{ncr.responsible_person}</span>
                            </div>
                          )}
                          {ncr.due_date && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Calendar className="w-3 h-3" />
                              <span>{formatDate(ncr.due_date)}</span>
                            </div>
                          )}
                        </div>

                        {/* Description Preview */}
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                          {ncr.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
        </>
      )}

        </div>
      </main>

      {/* Create NCR Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-slate-950 border-red-500/30 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-ncr-dialog">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Log New NCR
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Create a new non-conformance report.</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateNcr} className="space-y-4">
            {/* NCR Type and Severity */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Type *</Label>
                <Select
                  value={newNcr.ncr_type}
                  onValueChange={(val) => setNewNcr(prev => ({ ...prev, ncr_type: val }))}
                >
                  <SelectTrigger className="bg-slate-950/50 border-slate-800" data-testid="ncr-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.types?.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white uppercase tracking-wider text-xs mb-2">Severity *</Label>
                <Select
                  value={newNcr.severity}
                  onValueChange={(val) => setNewNcr(prev => ({ ...prev, severity: val }))}
                >
                  <SelectTrigger className="bg-muted/50 border-border" data-testid="ncr-severity-select">
                    <SelectValue placeholder="Select severity" />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.severities?.map((sev) => (
                      <SelectItem key={sev} value={sev}>{sev}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Source and Department */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white uppercase tracking-wider text-xs mb-2">Source *</Label>
                <Select
                  value={newNcr.source}
                  onValueChange={(val) => setNewNcr(prev => ({ ...prev, source: val }))}
                >
                  <SelectTrigger className="bg-muted/50 border-border" data-testid="ncr-source-select">
                    <SelectValue placeholder="Select source" />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.sources?.map((src) => (
                      <SelectItem key={src} value={src}>{src}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white uppercase tracking-wider text-xs mb-2">Department *</Label>
                <Select
                  value={newNcr.department}
                  onValueChange={(val) => setNewNcr(prev => ({ ...prev, department: val }))}
                >
                  <SelectTrigger className="bg-muted/50 border-border" data-testid="ncr-department-select">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {config?.departments?.map((dept) => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Raised By and Responsible Person */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white uppercase tracking-wider text-xs mb-2">Raised By *</Label>
                <Input
                  value={newNcr.raised_by}
                  onChange={(e) => setNewNcr(prev => ({ ...prev, raised_by: e.target.value }))}
                  className="bg-muted/50 border-border"
                  placeholder="Name of person raising NCR"
                  data-testid="ncr-raised-by-input"
                />
              </div>
              <div>
                <Label className="text-white uppercase tracking-wider text-xs mb-2">Responsible Person</Label>
                <Input
                  value={newNcr.responsible_person}
                  onChange={(e) => setNewNcr(prev => ({ ...prev, responsible_person: e.target.value }))}
                  className="bg-muted/50 border-border"
                  placeholder="Person responsible for resolution"
                  data-testid="ncr-responsible-input"
                />
              </div>
            </div>

            {/* References */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white uppercase tracking-wider text-xs mb-2">Work Order Reference</Label>
                <Input
                  value={newNcr.work_order_reference}
                  onChange={(e) => setNewNcr(prev => ({ ...prev, work_order_reference: e.target.value }))}
                  className="bg-muted/50 border-border"
                  placeholder="Related work order number"
                  data-testid="ncr-wo-ref-input"
                />
              </div>
              <div>
                <Label className="text-white uppercase tracking-wider text-xs mb-2">Customer Reference</Label>
                <Input
                  value={newNcr.customer_reference}
                  onChange={(e) => setNewNcr(prev => ({ ...prev, customer_reference: e.target.value }))}
                  className="bg-muted/50 border-border"
                  placeholder="Customer PO or reference"
                  data-testid="ncr-cust-ref-input"
                />
              </div>
            </div>

            {/* Supplier (for External NCRs) */}
            {newNcr.ncr_type === 'External' && (
              <div>
                <Label className="text-white uppercase tracking-wider text-xs mb-2">Supplier</Label>
                <Input
                  value={newNcr.supplier}
                  onChange={(e) => setNewNcr(prev => ({ ...prev, supplier: e.target.value }))}
                  className="bg-muted/50 border-border"
                  placeholder="Supplier name"
                  data-testid="ncr-supplier-input"
                />
              </div>
            )}

            {/* Due Date */}
            <div>
              <Label className="text-white uppercase tracking-wider text-xs mb-2">Due Date</Label>
              <Input
                type="date"
                value={newNcr.due_date}
                onChange={(e) => setNewNcr(prev => ({ ...prev, due_date: e.target.value }))}
                className="bg-muted/50 border-border"
                data-testid="ncr-due-date-input"
              />
            </div>

            {/* Description */}
            <div>
              <Label className="text-white uppercase tracking-wider text-xs mb-2">Description *</Label>
              <Textarea
                value={newNcr.description}
                onChange={(e) => setNewNcr(prev => ({ ...prev, description: e.target.value }))}
                className="bg-muted/50 border-border min-h-[100px]"
                placeholder="Describe the non-conformance in detail..."
                data-testid="ncr-description-input"
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
                className="border-border hover:border-primary"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-wide"
                data-testid="submit-ncr-btn"
              >
                Log NCR
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* NCR Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="ncr-detail-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-white uppercase tracking-tight text-xl flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                {selectedNcr?.ncr_number}
              </DialogTitle>
              <div className="flex items-center gap-2">
                {selectedNcr && (
                  <>
                    <span className={`text-xs px-2 py-1 rounded-sm font-bold ${SEVERITY_COLORS[selectedNcr.severity]}`}>
                      {selectedNcr.severity}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-sm font-bold ${
                      selectedNcr.ncr_type === 'External' ? 'bg-purple-950 text-purple-400' : 'bg-blue-950 text-blue-400'
                    }`}>
                      {selectedNcr.ncr_type}
                    </span>
                  </>
                )}
              </div>
            </div>
          </DialogHeader>

          {selectedNcr && (
            <div className="space-y-6">
              {/* Current Status */}
              <div className={`${STAGE_COLORS[selectedNcr.status]} border rounded-sm p-4`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Current Status</p>
                    <p className="text-white font-bold text-lg">{selectedNcr.status}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* External Supplier Link Button (only for External NCRs) */}
                    {selectedNcr.ncr_type === 'External' && (
                      <Button
                        onClick={handleGenerateExternalLink}
                        disabled={generatingLink}
                        variant="outline"
                        className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                        data-testid="generate-external-link-btn"
                      >
                        {generatingLink ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Link2 className="w-4 h-4 mr-1" />
                        )}
                        Supplier Link
                      </Button>
                    )}
                    {selectedNcr.status !== 'Closed' && (
                      <Button
                        onClick={handleAdvanceStage}
                        className="bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wide"
                        data-testid="advance-stage-btn"
                      >
                        <ChevronRight className="w-4 h-4 mr-1" />
                        Advance Stage
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Source</p>
                  <p className="text-white font-bold">{selectedNcr.source}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Department</p>
                  <p className="text-white font-bold">{selectedNcr.department}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Raised By</p>
                  <p className="text-white font-bold">{selectedNcr.raised_by}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Date Raised</p>
                  <p className="text-white font-bold">{formatDate(selectedNcr.date_raised)}</p>
                </div>
                {selectedNcr.responsible_person && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Responsible Person</p>
                    <p className="text-white font-bold">{selectedNcr.responsible_person}</p>
                  </div>
                )}
                {selectedNcr.due_date && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">Due Date</p>
                    <p className="text-white font-bold">{formatDate(selectedNcr.due_date)}</p>
                  </div>
                )}
              </div>

              {/* References */}
              {(selectedNcr.work_order_reference || selectedNcr.customer_reference || selectedNcr.supplier) && (
                <div className="grid grid-cols-3 gap-4 bg-muted/20 p-4 rounded-sm border border-border">
                  {selectedNcr.work_order_reference && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Work Order</p>
                      <p className="text-white font-mono font-bold">{selectedNcr.work_order_reference}</p>
                    </div>
                  )}
                  {selectedNcr.customer_reference && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Customer Ref</p>
                      <p className="text-white font-mono font-bold">{selectedNcr.customer_reference}</p>
                    </div>
                  )}
                  {selectedNcr.supplier && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider">Supplier</p>
                      <p className="text-white font-bold">{selectedNcr.supplier}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Description */}
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Description</p>
                <div className="bg-muted/20 border border-border rounded-sm p-4">
                  <p className="text-white whitespace-pre-wrap">{selectedNcr.description}</p>
                </div>
              </div>

              {/* Containment Action */}
              <div className="border border-orange-800 rounded-sm p-4 bg-orange-950/20">
                <h4 className="text-orange-400 font-bold uppercase tracking-wide mb-3 text-sm flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Containment Action
                </h4>
                <div className="space-y-3">
                  <div>
                    <Label className="text-white uppercase tracking-wider text-xs mb-1">Action Taken</Label>
                    <Textarea
                      value={editFormData?.containment?.action || ''}
                      onChange={(e) => handleFormChange('containment.action', e.target.value)}
                      className="bg-muted/50 border-border min-h-[60px]"
                      placeholder="Describe immediate containment actions..."
                      data-testid="containment-action-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-white uppercase tracking-wider text-xs mb-1">Responsible Person</Label>
                      <Input
                        value={editFormData?.containment?.responsible_person || ''}
                        onChange={(e) => handleFormChange('containment.responsible_person', e.target.value)}
                        className="bg-muted/50 border-border"
                        placeholder="Person responsible"
                      />
                    </div>
                    <div>
                      <Label className="text-white uppercase tracking-wider text-xs mb-1">Date Completed</Label>
                      <Input
                        type="date"
                        value={editFormData?.containment?.date_completed || ''}
                        onChange={(e) => handleFormChange('containment.date_completed', e.target.value)}
                        className="bg-muted/50 border-border"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* 5 Why Analysis */}
              <div className="border border-yellow-800 rounded-sm p-4 bg-yellow-950/20">
                <h4 className="text-yellow-400 font-bold uppercase tracking-wide mb-3 text-sm">
                  5 Why Root Cause Analysis
                </h4>
                <div className="space-y-3">
                  {['why1', 'why2', 'why3', 'why4', 'why5'].map((why, idx) => (
                    <div key={why}>
                      <Label className="text-white uppercase tracking-wider text-xs mb-1">Why {idx + 1}</Label>
                      <Input
                        value={editFormData?.five_why?.[why] || ''}
                        onChange={(e) => handleFormChange(`five_why.${why}`, e.target.value)}
                        className="bg-muted/50 border-border"
                        placeholder={`Why ${idx + 1}...`}
                      />
                    </div>
                  ))}
                  <div>
                    <Label className="text-yellow-400 uppercase tracking-wider text-xs mb-1 font-bold">Root Cause</Label>
                    <Textarea
                      value={editFormData?.five_why?.root_cause || ''}
                      onChange={(e) => handleFormChange('five_why.root_cause', e.target.value)}
                      className="bg-muted/50 border-yellow-600 min-h-[60px]"
                      placeholder="Identified root cause..."
                      data-testid="root-cause-input"
                    />
                  </div>
                </div>
              </div>

              {/* Corrective Action */}
              <div className="border border-blue-800 rounded-sm p-4 bg-blue-950/20">
                <h4 className="text-blue-400 font-bold uppercase tracking-wide mb-3 text-sm">
                  Corrective Action
                </h4>
                <div className="space-y-3">
                  <div>
                    <Label className="text-white uppercase tracking-wider text-xs mb-1">Corrective Action</Label>
                    <Textarea
                      value={editFormData?.corrective_action?.action || ''}
                      onChange={(e) => handleFormChange('corrective_action.action', e.target.value)}
                      className="bg-muted/50 border-border min-h-[60px]"
                      placeholder="Describe corrective action..."
                      data-testid="corrective-action-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-white uppercase tracking-wider text-xs mb-1">Responsible Person</Label>
                      <Input
                        value={editFormData?.corrective_action?.responsible_person || ''}
                        onChange={(e) => handleFormChange('corrective_action.responsible_person', e.target.value)}
                        className="bg-muted/50 border-border"
                        placeholder="Person responsible"
                      />
                    </div>
                    <div>
                      <Label className="text-white uppercase tracking-wider text-xs mb-1">Target Date</Label>
                      <Input
                        type="date"
                        value={editFormData?.corrective_action?.target_date || ''}
                        onChange={(e) => handleFormChange('corrective_action.target_date', e.target.value)}
                        className="bg-muted/50 border-border"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white uppercase tracking-wider text-xs mb-1">Implementation Notes</Label>
                    <Textarea
                      value={editFormData?.corrective_action?.implementation_notes || ''}
                      onChange={(e) => handleFormChange('corrective_action.implementation_notes', e.target.value)}
                      className="bg-muted/50 border-border min-h-[60px]"
                      placeholder="Implementation notes..."
                    />
                  </div>
                </div>
              </div>

              {/* Supplier Response Section (for External NCRs) */}
              {selectedNcr.ncr_type === 'External' && (
                <div className="border border-purple-800 rounded-sm p-4 bg-purple-950/20">
                  <h4 className="text-purple-400 font-bold uppercase tracking-wide mb-3 text-sm flex items-center gap-2">
                    <ExternalLink className="w-4 h-4" />
                    Supplier Response
                    {selectedNcr.supplier_response && (
                      <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded ml-2">
                        Received
                      </span>
                    )}
                  </h4>
                  {selectedNcr.supplier_response ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <span className="text-xs text-slate-500 uppercase">Submitted By</span>
                          <p className="text-white">{selectedNcr.supplier_response.submitted_by || '-'}</p>
                        </div>
                        <div>
                          <span className="text-xs text-slate-500 uppercase">Submitted Date</span>
                          <p className="text-white">{selectedNcr.supplier_response.submitted_at ? formatDate(selectedNcr.supplier_response.submitted_at) : '-'}</p>
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-slate-500 uppercase">Response</span>
                        <p className="text-white bg-slate-800/50 rounded p-2 mt-1 whitespace-pre-wrap">
                          {selectedNcr.supplier_response.response_text || '-'}
                        </p>
                      </div>
                      {selectedNcr.supplier_response.corrective_action && (
                        <div>
                          <span className="text-xs text-slate-500 uppercase">Supplier Corrective Action</span>
                          <p className="text-white bg-slate-800/50 rounded p-2 mt-1 whitespace-pre-wrap">
                            {selectedNcr.supplier_response.corrective_action}
                          </p>
                        </div>
                      )}
                      {selectedNcr.supplier_response.preventive_action && (
                        <div>
                          <span className="text-xs text-slate-500 uppercase">Supplier Preventive Action</span>
                          <p className="text-white bg-slate-800/50 rounded p-2 mt-1 whitespace-pre-wrap">
                            {selectedNcr.supplier_response.preventive_action}
                          </p>
                        </div>
                      )}
                      {selectedNcr.supplier_response.target_completion_date && (
                        <div>
                          <span className="text-xs text-slate-500 uppercase">Target Completion Date</span>
                          <p className="text-white">{formatDate(selectedNcr.supplier_response.target_completion_date)}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-slate-500 text-sm mb-3">No supplier response received yet.</p>
                      <Button
                        onClick={handleGenerateExternalLink}
                        disabled={generatingLink}
                        variant="outline"
                        className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                      >
                        {generatingLink ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Link2 className="w-4 h-4 mr-1" />
                        )}
                        Generate Supplier Link
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Verification */}
              <div className="border border-green-800 rounded-sm p-4 bg-green-950/20">
                <h4 className="text-green-400 font-bold uppercase tracking-wide mb-3 text-sm flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Verification of Effectiveness
                </h4>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-white uppercase tracking-wider text-xs mb-1">Verified By</Label>
                      <Input
                        value={editFormData?.verification?.verified_by || ''}
                        onChange={(e) => handleFormChange('verification.verified_by', e.target.value)}
                        className="bg-muted/50 border-border"
                        placeholder="Person who verified"
                      />
                    </div>
                    <div>
                      <Label className="text-white uppercase tracking-wider text-xs mb-1">Verification Date</Label>
                      <Input
                        type="date"
                        value={editFormData?.verification?.verification_date || ''}
                        onChange={(e) => handleFormChange('verification.verification_date', e.target.value)}
                        className="bg-muted/50 border-border"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-white uppercase tracking-wider text-xs mb-1">Verification Notes</Label>
                    <Textarea
                      value={editFormData?.verification?.notes || ''}
                      onChange={(e) => handleFormChange('verification.notes', e.target.value)}
                      className="bg-muted/50 border-border min-h-[60px]"
                      placeholder="Verification notes..."
                    />
                  </div>
                </div>
              </div>

              {/* Attachments Section */}
              <div className="border border-cyan-800 rounded-sm p-4 bg-cyan-950/20">
                <h4 className="text-cyan-400 font-bold uppercase tracking-wide mb-3 text-sm flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Attachments & Evidence
                  {selectedNcr.attachments && selectedNcr.attachments.length > 0 && (
                    <span className="text-xs bg-cyan-500/20 px-2 py-0.5 rounded">
                      {selectedNcr.attachments.length} files
                    </span>
                  )}
                </h4>
                
                {/* Upload Button */}
                <div className="mb-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileUpload}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    className="hidden"
                    data-testid="file-upload-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile}
                    className="border-cyan-600 text-cyan-400 hover:bg-cyan-950/50"
                  >
                    {uploadingFile ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload Evidence
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-slate-500 mt-1">
                    Supported: Images, PDF, Word, Excel (max 10MB)
                  </p>
                </div>

                {/* Attachments List */}
                {selectedNcr.attachments && selectedNcr.attachments.length > 0 ? (
                  <div className="space-y-2">
                    {selectedNcr.attachments.map((attachment) => (
                      <div
                        key={attachment.attachment_id}
                        className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg border border-slate-700"
                      >
                        <div className="flex items-center gap-3">
                          {attachment.file_type === 'image' ? (
                            <Image className="w-5 h-5 text-green-400" />
                          ) : (
                            <FileText className="w-5 h-5 text-blue-400" />
                          )}
                          <div>
                            <p className="text-white text-sm font-medium truncate max-w-[200px]">
                              {attachment.original_filename}
                            </p>
                            <p className="text-xs text-slate-500">
                              {formatFileSize(attachment.file_size)} • {formatDate(attachment.uploaded_at)}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownloadAttachment(attachment)}
                            className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-950/50"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteAttachment(attachment)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-950/50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm text-center py-4">
                    No attachments yet. Upload evidence photos or documents.
                  </p>
                )}
              </div>

              {/* Audit Trail */}
              {selectedNcr.audit_trail && selectedNcr.audit_trail.length > 0 && (
                <div className="border border-border rounded-sm p-4 bg-muted/10">
                  <h4 className="text-muted-foreground font-bold uppercase tracking-wide mb-3 text-sm">
                    Audit Trail
                  </h4>
                  <div className="space-y-2 max-h-[200px] overflow-y-auto">
                    {selectedNcr.audit_trail.map((entry, idx) => (
                      <div key={idx} className="text-xs border-b border-border pb-2">
                        <div className="flex items-center justify-between">
                          <span className="text-white font-bold">{entry.action}</span>
                          <span className="text-muted-foreground">{formatDate(entry.timestamp)}</span>
                        </div>
                        <p className="text-muted-foreground">{entry.user}: {entry.details}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between gap-3 pt-4 border-t border-border">
                {/* Download Button - Only show for Closed NCRs */}
                <div>
                  {selectedNcr.status === 'Closed' && (
                    <Button
                      onClick={handleDownloadNcr}
                      variant="outline"
                      className="border-blue-600 text-blue-400 hover:bg-blue-950/50 font-bold uppercase tracking-wide"
                      data-testid="download-ncr-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Report
                    </Button>
                  )}
                </div>
                
                {/* Right side buttons */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setDetailDialogOpen(false)}
                    className="border-border hover:border-primary"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveChanges}
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wide"
                    data-testid="save-ncr-btn"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* External Link Dialog */}
      <Dialog open={showExternalLinkDialog} onOpenChange={setShowExternalLinkDialog}>
        <DialogContent className="bg-slate-900 border-purple-500/30 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
              <Link2 className="w-5 h-5 text-purple-400" />
              External Supplier Link
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Share this link with the supplier to allow them to view and respond to this NCR.
            </DialogDescription>
          </DialogHeader>
          {externalLink && (
            <div className="space-y-4">
              <div>
                <Label className="text-slate-400 uppercase text-xs mb-2 block">Shareable Link</Label>
                <div className="flex gap-2">
                  <Input
                    value={externalLink.full_url}
                    readOnly
                    className="bg-slate-800 border-slate-700 text-cyan-400 font-mono text-sm"
                  />
                  <Button
                    onClick={handleCopyLink}
                    className="bg-purple-600 hover:bg-purple-500"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">NCR Number:</span>
                  <span className="text-white font-mono">{externalLink.ncr_number}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Expires:</span>
                  <span className="text-white">{externalLink.expires_at ? formatDate(externalLink.expires_at) : 'Never'}</span>
                </div>
              </div>

              <div className="bg-amber-950/30 border border-amber-500/30 rounded-lg p-3">
                <p className="text-amber-200 text-sm">
                  <strong>Note:</strong> The supplier will be able to view NCR details and submit their response through this link.
                </p>
              </div>

              <div className="flex justify-between pt-2">
                <Button
                  variant="outline"
                  onClick={() => window.open(externalLink.full_url, '_blank')}
                  className="border-slate-600"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Preview Link
                </Button>
                <Button
                  onClick={() => setShowExternalLinkDialog(false)}
                  className="bg-slate-700 hover:bg-slate-600"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NcrKanbanBoard;
